/**
 * Debounced operations utilities for smooth chart interactions.
 * Provides debouncing for viewport snapping and other deferred operations.
 */

export interface DebouncedSnap {
  /** Schedule a snap operation after the delay */
  schedule: () => void;
  /** Cancel any pending snap operation */
  cancel: () => void;
  /** Check if a snap is pending */
  isPending: () => boolean;
}

/**
 * Create a debounced snap controller.
 * Used to delay viewport snapping until interaction ends.
 *
 * @param callback - Function to execute after debounce delay
 * @param delay - Delay in milliseconds (default: 150ms)
 */
export function createDebouncedSnap(
  callback: () => void,
  delay = 150
): DebouncedSnap {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return {
    schedule: () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        callback();
        timeoutId = null;
      }, delay);
    },

    cancel: () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    },

    isPending: () => timeoutId !== null,
  };
}

export interface MomentumState {
  velocity: number;
  animationId: number | null;
}

/**
 * Apply momentum scrolling with deceleration.
 * Used for touch pan gestures to provide smooth inertia.
 *
 * @param initialVelocity - Starting velocity in pixels per frame
 * @param onUpdate - Called each frame with the current velocity
 * @param onComplete - Called when momentum stops
 * @param friction - Deceleration factor per frame (0-1, default: 0.95)
 * @param minVelocity - Minimum velocity before stopping (default: 0.5)
 */
export function applyMomentum(
  initialVelocity: number,
  onUpdate: (velocity: number) => void,
  onComplete: () => void,
  friction = 0.95,
  minVelocity = 0.5
): { cancel: () => void } {
  let velocity = initialVelocity;
  let animationId: number | null = null;

  const animate = (): void => {
    if (Math.abs(velocity) < minVelocity) {
      animationId = null;
      onComplete();

      return;
    }

    onUpdate(velocity);
    velocity *= friction;
    animationId = requestAnimationFrame(animate);
  };

  animationId = requestAnimationFrame(animate);

  return {
    cancel: () => {
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    },
  };
}

export interface VelocityTracker {
  /** Record a position sample */
  track: (position: number) => void;
  /** Get the current velocity (pixels per millisecond) */
  getVelocity: () => number;
  /** Reset the tracker */
  reset: () => void;
}

/**
 * Track velocity for momentum calculations.
 * Samples positions over time to calculate velocity.
 *
 * @param sampleWindow - Time window for velocity calculation in ms (default: 100)
 */
export function createVelocityTracker(sampleWindow = 100): VelocityTracker {
  const samples: Array<{ position: number; time: number }> = [];

  return {
    track: (position: number) => {
      const now = performance.now();

      samples.push({ position, time: now });

      // Remove old samples outside the window
      while (samples.length > 0 && now - samples[0].time > sampleWindow) {
        samples.shift();
      }
    },

    getVelocity: () => {
      if (samples.length < 2) return 0;

      const first = samples[0];
      const last = samples.at(-1)!;
      const dt = last.time - first.time;

      if (dt === 0) return 0;

      return (last.position - first.position) / dt;
    },

    reset: () => {
      samples.length = 0;
    },
  };
}
